import { useCallback, useEffect, useRef, useState } from "react";
import {
  getActiveIdToken,
  refreshIdToken,
  signOut as cognitoSignOut,
} from "./cognito.js";

/**
 * Owner-session hook backed by a real Cognito session (Req 3.1, 3.4, 3.5).
 *
 * The actual sign-in (SRP email + password, first-login password change, and
 * the forgot-password flow) happens in {@link ./cognito} and is driven by the
 * login UI; this hook owns the resulting session for the admin shell:
 *
 *   - On mount it restores any persisted Cognito session (tokens live in
 *     `localStorage`, managed by the SDK) so a reload keeps the owner signed
 *     in, silently refreshing an expired ID token via the refresh token.
 *   - It holds the current ID-token JWT as `token`, which the admin API client
 *     sends as `Authorization: Bearer <token>` (Req 3.3, 14.3).
 *   - It proactively refreshes the ID token shortly before it expires so
 *     management calls never carry a stale token.
 *   - `signOut` clears the Cognito session (Req 3.5).
 */

/** Restore phase, so the shell can avoid flashing the login form on reload. */
export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AdminAuth {
  /** The current Cognito ID-token JWT, or null when signed out. */
  token: string | null;
  /** Whether the owner currently has a valid session. */
  isAuthenticated: boolean;
  /** "loading" while the persisted session is being restored on mount. */
  status: AuthStatus;
  /** Records a freshly obtained ID token after a successful sign-in. */
  setToken: (idToken: string) => void;
  /** Clears the Cognito session and the in-memory token (logout). */
  signOut: () => void;
}

/** Seconds before expiry at which we proactively refresh the ID token. */
const REFRESH_SKEW_SECONDS = 60;

/** Decodes a JWT's `exp` (seconds since epoch), or null if undecodable. */
function decodeExpSeconds(jwt: string): number | null {
  const parts = jwt.split(".");
  if (parts.length !== 3) {
    return null;
  }
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
    ) as { exp?: unknown };
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

export function useAdminAuth(): AdminAuth {
  const [token, setTokenState] = useState<string | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimer.current !== undefined) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = undefined;
    }
  }, []);

  /** Schedules a proactive refresh shortly before the token's expiry. */
  const scheduleRefresh = useCallback(
    (jwt: string) => {
      clearRefreshTimer();
      const exp = decodeExpSeconds(jwt);
      if (exp === null) {
        return;
      }
      const nowSeconds = Date.now() / 1000;
      const delayMs = Math.max(0, (exp - REFRESH_SKEW_SECONDS - nowSeconds) * 1000);
      refreshTimer.current = setTimeout(() => {
        void refreshIdToken().then((next) => {
          if (next !== null) {
            setTokenState(next);
            setStatus("authenticated");
            scheduleRefresh(next);
          } else {
            // Refresh token no longer valid → treat as expired session.
            setTokenState(null);
            setStatus("unauthenticated");
          }
        });
      }, delayMs);
    },
    [clearRefreshTimer],
  );

  const setToken = useCallback(
    (idToken: string) => {
      setTokenState(idToken);
      setStatus("authenticated");
      scheduleRefresh(idToken);
    },
    [scheduleRefresh],
  );

  const signOut = useCallback(() => {
    clearRefreshTimer();
    cognitoSignOut();
    setTokenState(null);
    setStatus("unauthenticated");
  }, [clearRefreshTimer]);

  // Restore a persisted session on mount (and refresh if the ID token expired).
  useEffect(() => {
    let active = true;
    void getActiveIdToken().then((restored) => {
      if (!active) {
        return;
      }
      if (restored !== null) {
        setTokenState(restored);
        setStatus("authenticated");
        scheduleRefresh(restored);
      } else {
        setStatus("unauthenticated");
      }
    });
    return () => {
      active = false;
      clearRefreshTimer();
    };
  }, [scheduleRefresh, clearRefreshTimer]);

  return {
    token,
    isAuthenticated: token !== null && token.length > 0,
    status,
    setToken,
    signOut,
  };
}
